import Swiper from './swiper.min.js';

/* Theme — Product media gallery (Swiper + deferred media) */

class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.config = {};
    this.swiper = null;
    this.ticker = null;
    this.lightbox = null;
    this.thumbSwipers = [];
    this.thumbs = [];
    this.swiperEl = null;
    this.paginationEl = null;
    this.variantSelect = null;
    this.sectionRoot = null;
    this.mainProductSection = null;
    this.isMainProductGallery = false;
    this.disableSliderDrag = false;
    this.viewportMediaSyncRafId = 0;
    this.chromeResizeObserver = null;
    this.variantMediaItems = [];
    this.variantMediaMap = [];
    this.onVariantChanged = this.onVariantChanged.bind(this);
    this.onVariantSelectChange = this.onVariantSelectChange.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onViewportResize = this.onViewportResize.bind(this);
  }

  connectedCallback() {
    this.config = this.readConfig();
    this.thumbs = Array.from(this.querySelectorAll('[data-media-index]'));
    this.swiperEl = this.querySelector('.product-gallery__swiper');
    this.paginationEl = this.querySelector('.product-gallery__pagination');
    this.sectionRoot = this.closest('.shopify-section') || this.parentElement || document;
    this.variantSelect = this.sectionRoot?.querySelector('.main-product__variant-select') || null;
    this.mainProductSection = this.closest('.main-product');
    this.isMainProductGallery = this.dataset.mainProductGallery === 'true';
    this.disableSliderDrag = this.dataset.disableSliderDrag === 'true';
    this.initVariantMediaFilter();

    this.bindThumbs();
    this.bindVariantSelect();
    this.listenForVariantChange();
    this.listenForEditorEvents();
    this.initLightbox();
    window.addEventListener('resize', this.onViewportResize, { passive: true });
    this.initMainProductViewportHeightSync();

    if (this.isCinematicMode()) {
      this.ticker = new CinematicTicker(this, this.config);
      this.ticker.init();
      return;
    }

    this.initThumbSwipers();

    if (!this.shouldInitSwiper()) {
      this.updateActiveThumbnail(0);
      return;
    }

    this.initSwiper();
  }

  disconnectedCallback() {
    this.releaseInitialSlideLock();

    this.querySelectorAll('deferred-media').forEach((media) => {
      if (typeof media.pause === 'function') {
        media.pause();
      }
    });

    this.ticker?.destroy();
    this.ticker = null;
    this.destroyLightbox();
    this.destroySwiper();
    this.destroyThumbSwipers();
    document.removeEventListener('variant:changed', this.onVariantChanged);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    this.variantSelect?.removeEventListener('change', this.onVariantSelectChange);
    window.removeEventListener('resize', this.onViewportResize);
    this.teardownMainProductViewportHeightSync();
  }

  isCinematicMode() {
    return this.dataset.galleryMode === 'ticker' || this.dataset.galleryMode === 'cinematic_ticker';
  }

  shouldInitSwiper() {
    const mode = this.dataset.galleryMode;
    const isSliderMode = mode === 'slider' || mode === 'slider_with_thumbnails';
    const slideCount = this.querySelectorAll('.product-gallery__slide').length;
    return Boolean(this.swiperEl && Swiper && isSliderMode && slideCount > 1);
  }

  readConfig() {
    const configNode = this.querySelector('[data-gallery-config]');
    if (!configNode) {
      return {
        speed: 400,
        loop: false,
        threshold: 10,
        a11y: true,
        showMobileNav: true,
        desktopSlidesPerView: 1,
        tickerSpeed: 60,
        tickerDirection: 'left',
        tickerPauseOnHover: false,
        tickerEnableDrag: true
      };
    }

    try {
      const parsed = JSON.parse(configNode.textContent);
      const desktopSlidesRaw = parsed.desktopSlidesPerView;
      const desktopSlidesPerView = desktopSlidesRaw === 'auto'
        ? 'auto'
        : (Number(desktopSlidesRaw) > 1 ? 2 : 1);
      return {
        speed: Number(parsed.speed) || 400,
        loop: Boolean(parsed.loop),
        threshold: Number(parsed.threshold) || 10,
        a11y: parsed.a11y !== false,
        showMobileNav: parsed.showMobileNav !== false,
        desktopSlidesPerView,
        tickerSpeed: Number(parsed.tickerSpeed) || 60,
        tickerDirection: parsed.tickerDirection === 'right' ? 'right' : 'left',
        tickerPauseOnHover: parsed.tickerPauseOnHover === true,
        tickerEnableDrag: parsed.tickerEnableDrag !== false,
      };
    } catch (_error) {
      return {
        speed: 400,
        loop: false,
        threshold: 10,
        a11y: true,
        showMobileNav: true,
        desktopSlidesPerView: 1,
        tickerSpeed: 60,
        tickerDirection: 'left',
        tickerPauseOnHover: false,
        tickerEnableDrag: true
      };
    }
  }

  hasZoomItems() {
    return this.dataset.zoomEnabled === 'true' && Boolean(this.querySelector('a[data-pswp-item]'));
  }

  async initLightbox() {
    if (this.lightbox || !this.hasZoomItems()) return;

    try {
      const module = await import('./photoswipe-lightbox.esm.min.js');
      const PhotoSwipeLightbox = module?.default;
      if (!PhotoSwipeLightbox) return;

      this.lightbox = new PhotoSwipeLightbox({
        gallery: this,
        // With the variant media filter on, hidden media stays out of the lightbox.
        children: this.isVariantMediaFilterEnabled()
          ? '[data-variant-media]:not([hidden]) a[data-pswp-item]'
          : 'a[data-pswp-item]',
        bgOpacity: 1,
        wheelToZoom: true,
        pswpModule: () => import('./photoswipe.esm.min.js'),
      });
      this.lightbox.init();
    } catch (_error) {
      this.lightbox = null;
    }
  }

  destroyLightbox() {
    if (!this.lightbox) return;
    this.lightbox.destroy();
    this.lightbox = null;
  }

  refreshLightbox() {
    this.destroyLightbox();
    this.initLightbox();
  }

  initSwiper() {
    const SwiperClass = Swiper;
    if (!SwiperClass) return;
    this.swiperEl.classList.add('is-ready');
    const sliderGap = this.dataset.galleryMode === 'slider' ? 3 : 0;
    const desktopSlidesPerView = this.resolveDesktopSlidesPerView();
    const enableDesktopFreeView = desktopSlidesPerView === 'auto' && !this.disableSliderDrag;
    const transitionSpeed = this.isMainProductGallery ? 550 : this.config.speed;

    // Open on the pre-selected variant's media (set in Liquid) rather than the
    // product's first media, so the opening image matches the active variant.
    let initialSlide = 0;
    const initialMediaId = this.dataset.initialMediaId;
    if (initialMediaId) {
      const initialIndex = this.findMediaIndex(initialMediaId);
      if (initialIndex >= 0) initialSlide = initialIndex;
    }

    const options = {
      speed: transitionSpeed,
      initialSlide,
      loop: this.config.loop,
      threshold: this.config.threshold,
      watchSlidesProgress: true,
      allowTouchMove: !this.disableSliderDrag,
      simulateTouch: !this.disableSliderDrag,
      shortSwipes: !this.disableSliderDrag,
      longSwipes: !this.disableSliderDrag,
      followFinger: !this.disableSliderDrag,
      grabCursor: false,
      slidesPerView: 1,
      spaceBetween: sliderGap,
      breakpoints: {
        1000: {
          slidesPerView: desktopSlidesPerView,
          spaceBetween: sliderGap,
          freeMode: enableDesktopFreeView
            ? {
                enabled: true,
                momentum: true,
                sticky: false,
                momentumRatio: 0.35,
              }
            : false,
        },
      },
      navigation: {
        nextEl: this.querySelector('.product-gallery__nav--next'),
        prevEl: this.querySelector('.product-gallery__nav--prev'),
      },
      a11y: this.config.a11y ? { enabled: true } : false,
      on: {
        init: () => {
          const activeIndex = this.swiper?.activeIndex || 0;
          this.updateActiveThumbnail(activeIndex);
          this.pauseInactiveMedia(activeIndex);
        },
        slideChange: () => {
          const activeIndex = this.swiper?.activeIndex || 0;
          this.updateActiveThumbnail(activeIndex);
          this.pauseInactiveMedia(activeIndex);
        },
      },
    };

    if (!this.isMainProductGallery && this.paginationEl) {
      options.pagination = {
        el: this.paginationEl,
        clickable: true,
      };
    }

    if (!this.config.showMobileNav) {
      const prev = this.querySelector('.product-gallery__nav--prev');
      const next = this.querySelector('.product-gallery__nav--next');
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }

    this.swiper = new SwiperClass(this.swiperEl, options);

    // Swiper's `init` callback fires before `this.swiper` is assigned, so it
    // reads activeIndex as 0 and mis-highlights the first thumbnail when the
    // gallery opens on a non-first slide (e.g. a pre-selected variant's media).
    // Re-sync now that the instance exists.
    this.updateActiveThumbnail(this.swiper.activeIndex);
    this.pauseInactiveMedia(this.swiper.activeIndex);

    if (this.isMainProductGallery && this.swiper?.wrapperEl) {
      this.swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    }

    // Swiper re-runs update() as lazy media (e.g. a 3D-model poster) finishes
    // loading, which can snap the active slide away from initialSlide on a cold
    // load. Briefly re-assert the intended opening slide until the shopper
    // interacts, so the gallery reliably opens on the selected variant's media.
    this.enforceInitialSlide(initialSlide);
  }

  enforceInitialSlide(index) {
    this.releaseInitialSlideLock();
    if (!this.swiper || !(index >= 0)) return;

    // Swiper re-runs update() as lazy media finishes loading, which re-snaps the
    // active slide away from initialSlide. Image loads are network-timed, so a
    // fixed time window isn't enough — instead re-assert the intended slide on
    // every layout update until the shopper actually navigates the gallery.
    // slideTo(..., false) runs without callbacks so it never registers as user
    // navigation; the thumbnail/media state is synced manually.
    const reassert = () => {
      if (!this.swiper || this.swiper.activeIndex === index) return;
      this.swiper.slideTo(index, 0, false);
      this.updateActiveThumbnail(index);
      this.pauseInactiveMedia(index);
    };
    const release = () => this.releaseInitialSlideLock();

    this.swiper.on('update', reassert);
    // Release only on genuine shopper interaction. (slideChange can't be used —
    // Swiper's own update() fires it via an internal slideTo, which would
    // release the lock on the very drift we're guarding against.) Thumbnail and
    // variant navigation call releaseInitialSlideLock() directly.
    this.swiperEl.addEventListener('pointerdown', release, true);
    this.swiperEl.addEventListener('keydown', release, true);

    this._initialSlideLock = () => {
      this.swiper?.off('update', reassert);
      this.swiperEl.removeEventListener('pointerdown', release, true);
      this.swiperEl.removeEventListener('keydown', release, true);
      this._initialSlideLock = null;
    };
  }

  releaseInitialSlideLock() {
    if (this._initialSlideLock) this._initialSlideLock();
  }

  resolveDesktopSlidesPerView() {
    if (this.config.desktopSlidesPerView === 'auto') return 'auto';
    if (this.config.desktopSlidesPerView === 2) return 2;
    return 1;
  }

  onViewportResize() {
    this.scheduleMainProductViewportHeightSync();
    if (!this.swiper || this.isCinematicMode()) return;

    const nextDesktopSpv = this.resolveDesktopSlidesPerView();
    const breakpoints = this.swiper.params.breakpoints || {};
    const currentDesktopSpv = breakpoints[1000]?.slidesPerView ?? 1;
    const currentDesktopFreeMode = breakpoints[1000]?.freeMode;
    const currentDesktopFreeModeEnabled = currentDesktopFreeMode === true || Boolean(currentDesktopFreeMode?.enabled);
    const nextDesktopFreeModeEnabled = nextDesktopSpv === 'auto' && !this.disableSliderDrag;
    if (currentDesktopSpv === nextDesktopSpv && currentDesktopFreeModeEnabled === nextDesktopFreeModeEnabled) return;

    this.swiper.params.breakpoints = {
      ...breakpoints,
      1000: {
        ...(breakpoints[1000] || {}),
        slidesPerView: nextDesktopSpv,
        freeMode: nextDesktopFreeModeEnabled
          ? {
              enabled: true,
              momentum: true,
              sticky: false,
              momentumRatio: 0.35,
            }
          : false,
      },
    };
    this.swiper.update();
  }

  initMainProductViewportHeightSync() {
    if (!this.isMainProductGallery) return;
    this.syncMainProductViewportHeight();

    if ('ResizeObserver' in window) {
      this.chromeResizeObserver = new ResizeObserver(() => this.scheduleMainProductViewportHeightSync());
      this.getViewportChromeElements().forEach((element) => this.chromeResizeObserver?.observe(element));
    }
  }

  teardownMainProductViewportHeightSync() {
    if (this.viewportMediaSyncRafId) {
      window.cancelAnimationFrame(this.viewportMediaSyncRafId);
      this.viewportMediaSyncRafId = 0;
    }

    this.chromeResizeObserver?.disconnect();
    this.chromeResizeObserver = null;
  }

  scheduleMainProductViewportHeightSync() {
    if (!this.isMainProductGallery) return;
    if (this.viewportMediaSyncRafId) return;

    this.viewportMediaSyncRafId = window.requestAnimationFrame(() => {
      this.viewportMediaSyncRafId = 0;
      this.syncMainProductViewportHeight();
    });
  }

  getViewportChromeElements() {
    const headerEl = document.querySelector('header[data-section-type="header"]');
    const announcementEl = document.querySelector('[data-announcement-bar]');
    const elements = [];

    if (announcementEl instanceof HTMLElement) {
      elements.push(announcementEl);
    }
    if (headerEl instanceof HTMLElement) {
      elements.push(headerEl);
    }

    return elements;
  }

  getStaticElementHeight(element) {
    if (!(element instanceof HTMLElement)) return 0;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return 0;

    const rect = element.getBoundingClientRect();
    const elementHeight = Math.max(element.offsetHeight || 0, rect.height || 0);
    return elementHeight > 0 ? elementHeight : 0;
  }

  syncMainProductViewportHeight() {
    if (!this.isMainProductGallery || !(this.mainProductSection instanceof HTMLElement)) return;

    const isDesktop = window.matchMedia('(min-width: 1000px)').matches;
    if (!isDesktop) {
      this.mainProductSection.style.removeProperty('--main-product-chrome-offset');
      this.mainProductSection.style.removeProperty('--main-product-media-target-height');
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (viewportHeight <= 0) return;

    const chromeOffset = this.getViewportChromeElements().reduce((sum, element) => {
      return sum + this.getStaticElementHeight(element);
    }, 0);

    const normalizedOffset = Math.max(0, Math.round(chromeOffset));
    const mediaHeight = Math.max(320, Math.round(viewportHeight - normalizedOffset));

    const newOffsetVal = `${normalizedOffset}px`;
    const newHeightVal = `${mediaHeight}px`;
    if (
      this.mainProductSection.style.getPropertyValue('--main-product-chrome-offset') === newOffsetVal &&
      this.mainProductSection.style.getPropertyValue('--main-product-media-target-height') === newHeightVal
    ) return;

    this.mainProductSection.style.setProperty('--main-product-chrome-offset', newOffsetVal);
    this.mainProductSection.style.setProperty('--main-product-media-target-height', newHeightVal);
  }

  destroySwiper() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
    this.swiperEl?.classList.remove('is-ready');
  }

  initThumbSwipers() {
    this.destroyThumbSwipers();
    const rails = Array.from(this.querySelectorAll('.product-gallery__thumbs.swiper'));
    if (!rails.length || !Swiper) return;

    this.thumbSwipers = rails.map((rail) => {
      const isVertical = rail.closest('.product-gallery--thumbs-left') && !rail.closest('.product-gallery--thumbs-bottom');
      return new Swiper(rail, {
        slidesPerView: 'auto',
        direction: isVertical ? 'vertical' : 'horizontal',
        spaceBetween: 4,
        watchOverflow: true,
        threshold: 6
      });
    });
  }

  destroyThumbSwipers() {
    if (!this.thumbSwipers.length) return;
    this.thumbSwipers.forEach((instance) => instance?.destroy?.(true, true));
    this.thumbSwipers = [];
  }

  bindThumbs() {
    if (!this.thumbs.length) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.mediaIndex);
        if (Number.isNaN(index)) return;

        if (this.swiper) {
          this.swiper.slideTo(index);
        }

        this.updateActiveThumbnail(index);
      });
    });
  }

  updateActiveThumbnail(index) {
    if (!this.thumbs.length) return;

    this.thumbs.forEach((thumb) => {
      thumb.classList.remove('is-active');
      thumb.removeAttribute('aria-current');
    });

    const activeThumb = this.thumbs[index];
    if (!activeThumb) return;

    activeThumb.classList.add('is-active');
    activeThumb.setAttribute('aria-current', 'true');

    this.thumbSwipers.forEach((instance) => {
      if (instance?.destroyed) return;
      instance.slideTo(index);
    });
  }

  goToMediaById(mediaId) {
    if (!mediaId) return;

    // Any explicit navigation (variant change, thumbnail click) supersedes the
    // initial-slide lock so we don't yank the shopper back.
    this.releaseInitialSlideLock();

    if (this.isCinematicMode() && this.ticker) {
      this.ticker.scrollToMediaById(mediaId);
      return;
    }

    const index = this.findMediaIndex(mediaId);
    if (index === -1) return;

    if (this.swiper) {
      this.swiper.slideTo(index);
    } else {
      // Collage / stacked: scroll the matching item into view.
      const mediaEl = this.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      mediaEl?.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      this.pauseInactiveMedia(index);
    }

    this.updateActiveThumbnail(index);
  }

  pauseInactiveMedia(activeIndex = 0) {
    // Works for slider slides, collage grid-items, and stacked stack-items.
    const items = Array.from(
      this.querySelectorAll(
        '.product-gallery__slide, .product-gallery__grid-item, .product-gallery__stack-item, .ticker-rail__item:not(.ticker-rail__item--clone)'
      )
    );
    if (!items.length) return;

    items.forEach((item, index) => {
      if (index === activeIndex) return;
      const media = item.querySelector('deferred-media');
      if (!media || typeof media.pause !== 'function') return;
      media.pause();
    });
  }

  findMediaIndex(mediaId) {
    const normalizedId = String(mediaId);

    if (this.thumbs.length) {
      const thumb = this.thumbs.find((item) => String(item.dataset.mediaId) === normalizedId);
      if (thumb) return Number(thumb.dataset.mediaIndex);
    }

    // Cover slides (slider), grid-items (collage), and stack-items (stacked).
    const containers = this.querySelectorAll(
      '.product-gallery__slide, .product-gallery__grid-item, .product-gallery__stack-item'
    );
    return Array.from(containers).findIndex((el) => String(el.dataset.mediaId) === normalizedId);
  }

  listenForVariantChange() {
    document.addEventListener('variant:changed', this.onVariantChanged);
  }

  bindVariantSelect() {
    this.variantSelect?.addEventListener('change', this.onVariantSelectChange);
  }

  onVariantSelectChange(event) {
    const selected = event?.target?.selectedOptions?.[0];
    const mediaId = selected?.dataset?.featuredMediaId;
    if (!mediaId) return;
    this.goToMediaById(mediaId);
  }

  onVariantChanged(event) {
    const thisSectionId = this.dataset.sectionId;
    // Require a section ID on this gallery — avoids cross-section interference.
    if (!thisSectionId) return;

    const eventSectionId = event?.detail?.sectionId;
    if (eventSectionId && String(eventSectionId) !== String(thisSectionId)) return;

    this.filterVariantMedia(event?.detail?.variant);

    const mediaId = event?.detail?.variant?.featured_media?.id;
    if (!mediaId) return;
    this.goToMediaById(mediaId);
  }

  isVariantMediaFilterEnabled() {
    return this.hasAttribute('data-variant-media-filter');
  }

  initVariantMediaFilter() {
    this.variantMediaItems = [];
    this.variantMediaMap = [];
    if (!this.isVariantMediaFilterEnabled()) return;

    const mapNode = this.querySelector('[data-variant-media-map]');
    if (!mapNode) return;

    try {
      const map = JSON.parse(mapNode.textContent);
      if (!Array.isArray(map)) return;
      this.variantMediaMap = map;
    } catch (_error) {
      return;
    }

    this.variantMediaItems = Array.from(this.querySelectorAll('[data-variant-media]'));
  }

  // Mirrors the rule in snippets/product-media-gallery.liquid: untagged media
  // is shared; tagged media must name the selected value of every option it
  // names; if no tagged media qualifies, everything is shown.
  filterVariantMedia(variant) {
    if (!this.variantMediaItems.length || !Array.isArray(variant?.options)) return;

    const selectedTokens = variant.options.map((value, index) => this.variantMediaMap[index]?.[value] || null);
    let hasVisibleTaggedMedia = false;

    const visibility = this.variantMediaItems.map((item) => {
      const tokens = (item.dataset.variantMedia || '').split(',').filter(Boolean);
      const isVisible = tokens.every((token) => {
        const selectedToken = selectedTokens[Number(token.split(':')[0])];
        return !selectedToken || tokens.includes(selectedToken);
      });
      if (tokens.length && isVisible) hasVisibleTaggedMedia = true;
      return isVisible;
    });

    this.variantMediaItems.forEach((item, index) => {
      const shouldHide = hasVisibleTaggedMedia && !visibility[index];
      if (item.hidden === shouldHide) return;
      item.hidden = shouldHide;
      if (!shouldHide) return;

      const media = item.querySelector('deferred-media');
      if (media && typeof media.pause === 'function') media.pause();
    });
  }

  listenForEditorEvents() {
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  onSectionLoad(event) {
    if (!event?.target?.contains(this)) return;

    if (this.isCinematicMode()) {
      this.ticker?.destroy();
      this.ticker = null;
      this.config = this.readConfig();
      this.ticker = new CinematicTicker(this, this.config);
      this.ticker.init();
      this.refreshLightbox();
      return;
    }

    this.destroySwiper();
    this.destroyThumbSwipers();
    this.initThumbSwipers();
    this.config = this.readConfig();

    if (this.shouldInitSwiper()) {
      this.initSwiper();
    }

    this.refreshLightbox();
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   CinematicTicker
   RAF-driven infinite ticker rail with scroll coupling, drag inertia,
   hover/focus pause, IntersectionObserver, and reduced-motion fallback.
   ───────────────────────────────────────────────────────────────────────────── */

class CinematicTicker {
  constructor(galleryEl, config) {
    this.gallery = galleryEl;
    this.config = config;
    this.rail = null;
    this.track = null;
    this.items = [];
    this.originalItems = [];
    this.offset = 0;
    this.loopWidth = 0;
    this.baseVelocity = 0;
    this.configSpeed = 0;      // absolute speed magnitude from config — direction-change reference
    this.lerpedBase = 0;       // smoothed baseVelocity — avoids hard velocity jumps on direction change
    this.scrollBoost = 0;
    this.momentumVelocity = 0;
    this.maxVelocity = 480;
    this.lastScrollY = window.scrollY;
    this.rafId = null;
    this.lastTimestamp = 0;
    this.isAutoEnabled = false;
    this.isAutoActive = false;
    this.isPaused = false;
    this.isDragging = false;
    this.dragVelocity = 0;
    this.dragLastX = 0;
    this.dragLastTime = 0;
    this.dragMultiplier = 1.75;
    this.dragVelocitySmoothing = 0.18;
    this.maxDragStepPerFrame = 32;
    this.maxMomentumVelocity = 360;
    this.momentumDecay = 0.9;
    this.settlingTarget = null;
    this.defaultSettlingStrength = 0.16;
    this.variantSettlingStrength = 0.09;
    this.settlingStrength = this.defaultSettlingStrength;
    this.variantAutoFreezeMs = 10000;
    this.variantAutoFreezeUntil = 0;
    this.variantAwaitingInteraction = false;
    this.preVariantBaseVelocity = 0;
    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.isVisible = true;
    this.lastActiveIndex = -1;
    this.rebuildAttempts = 0;
    this.lastWheelTimestamp = 0;
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopQuery = window.matchMedia('(min-width: 1000px)');
    this.reduceMotion = this.reduceMotionQuery.matches;
    this.pointerId = null;

    // Bound handlers
    this._tick = this._tick.bind(this);
    this._onWheel = this._onWheel.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._onFocusIn = this._onFocusIn.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onModeChange = this._onModeChange.bind(this);
    this._onNativeScroll = this._onNativeScroll.bind(this);
  }

  init() {
    this.rail = this.gallery.querySelector('.ticker-rail');
    this.track = this.gallery.querySelector('[data-ticker-track]');
    if (!this.rail || !this.track) return;

    this.originalItems = Array.from(this.track.querySelectorAll('.ticker-rail__item:not(.ticker-rail__item--clone)'));
    if (!this.originalItems.length) return;

    this._configureBaseVelocity();
    this._refreshModeFlags();
    this._rebuildTrack({ keepOffset: false });
    this._bindResizeObserver();
    this._bindModeListeners();

    if (this.config.tickerEnableDrag) {
      this._bindDrag();
    }

    // Hover no longer pauses — ticker runs continuously regardless of setting.
    // _bindHover() is intentionally NOT called here.

    this._bindFocus();
    this._bindNativeScroll();

    if (this.isAutoEnabled) {
      this._enableAutoMotion();
    } else {
      this._syncNativeScrollPosition();
      this._setActiveItemFromOffset(true);
    }
  }

  // ── Setup helpers ────────────────────────────────────────────────────────

  _configureBaseVelocity() {
    const configuredSpeed = Number(this.config.tickerSpeed);
    const speed = Number.isFinite(configuredSpeed) ? Math.max(20, configuredSpeed) : 80;
    this.configSpeed = speed;
    this.baseVelocity = this.config.tickerDirection === 'right' ? speed : -speed;
    // Seed lerpedBase so the ticker starts at full speed with no initial ramp from 0.
    this.lerpedBase = this.baseVelocity;
  }

  _refreshModeFlags() {
    this.reduceMotion = this.reduceMotionQuery.matches;
    this.isAutoEnabled = !this.reduceMotion;
  }

  _bindModeListeners() {
    if (typeof this.reduceMotionQuery.addEventListener === 'function') {
      this.reduceMotionQuery.addEventListener('change', this._onModeChange);
    } else if (typeof this.reduceMotionQuery.addListener === 'function') {
      this.reduceMotionQuery.addListener(this._onModeChange);
    }

    if (typeof this.desktopQuery.addEventListener === 'function') {
      this.desktopQuery.addEventListener('change', this._onModeChange);
    } else if (typeof this.desktopQuery.addListener === 'function') {
      this.desktopQuery.addListener(this._onModeChange);
    }
  }

  _unbindModeListeners() {
    if (typeof this.reduceMotionQuery.removeEventListener === 'function') {
      this.reduceMotionQuery.removeEventListener('change', this._onModeChange);
    } else if (typeof this.reduceMotionQuery.removeListener === 'function') {
      this.reduceMotionQuery.removeListener(this._onModeChange);
    }

    if (typeof this.desktopQuery.removeEventListener === 'function') {
      this.desktopQuery.removeEventListener('change', this._onModeChange);
    } else if (typeof this.desktopQuery.removeListener === 'function') {
      this.desktopQuery.removeListener(this._onModeChange);
    }
  }

  _onModeChange() {
    this._refreshModeFlags();
    this._rebuildTrack({ keepOffset: true });

    if (this.isAutoEnabled) {
      this._enableAutoMotion();
    } else {
      this._disableAutoMotion();
      this._syncNativeScrollPosition();
      this._setActiveItemFromOffset(true);
    }
  }

  _bindResizeObserver() {
    if (!('ResizeObserver' in window)) return;
    this.resizeObserver = new ResizeObserver(this._onResize);
    this.resizeObserver.observe(this.rail);
  }

  _onResize() {
    this._rebuildTrack({ keepOffset: true });
    if (!this.isAutoEnabled) {
      this._applyTransform();
      this._setActiveItemFromOffset(true);
    }
  }

  _rebuildTrack({ keepOffset }) {
    if (!this.track) return;

    const previousLoopWidth = this.loopWidth;
    const previousRatio = keepOffset && previousLoopWidth ? this.offset / previousLoopWidth : 0;

    this.track.querySelectorAll('.ticker-rail__item--clone').forEach((clone) => clone.remove());
    this.originalItems = Array.from(this.track.querySelectorAll('.ticker-rail__item:not(.ticker-rail__item--clone)'));
    this.items = [...this.originalItems];

    if (!this.originalItems.length) {
      this.loopWidth = 0;
      return;
    }

    const railWidth = this.rail.getBoundingClientRect().width || window.innerWidth;

    // ── Explicit px widths ────────────────────────────────────────────────────
    // When .is-ready adds width:max-content to the track, CSS flex-basis:% becomes
    // circular (% of max-content → items inflate to intrinsic size → max-content
    // grows → %). Resolve by computing the correct pixel width from the rail and
    // forcing it onto each item as an absolute value.
    const isDesktop = this.desktopQuery.matches;
    const sectionEl = this.gallery.closest('[id^="shopify-section"]') || document.documentElement;
    const sectionStyle = getComputedStyle(sectionEl);
    const pctStr = isDesktop
      ? sectionStyle.getPropertyValue('--ticker-item-width-desktop').trim()
      : sectionStyle.getPropertyValue('--ticker-item-width-mobile').trim();
    const itemPct = (parseFloat(pctStr) || (isDesktop ? 28 : 80)) / 100;
    let itemWidthPx = Math.floor(railWidth * itemPct);

    // ── Aspect-ratio override ─────────────────────────────────────────────────
    // When a desktop media ratio is set (e.g. 1:1, 4:5), derive the item width
    // from railHeight × ratio so images sit perfectly in the fixed-height stage
    // without letterboxing.  Only applied on desktop where the rail has a definite
    // svh height; mobile keeps the percentage-based sizing.
    if (isDesktop) {
      const railHeight = this.rail.getBoundingClientRect().height;
      if (railHeight) {
        const firstWrap = this.originalItems[0]?.querySelector(
          '.product-media__image-wrap, .product-media__deferred'
        );
        if (firstWrap) {
          const ratioRaw = getComputedStyle(firstWrap)
            .getPropertyValue('--media-ratio')
            .trim();
          // Expect a string like "1 / 1" or "4 / 5"
          const parts = ratioRaw.split('/').map((s) => parseFloat(s.trim()));
          if (parts.length === 2 && parts[1]) {
            const ratioWidth = Math.floor(railHeight * (parts[0] / parts[1]));
            // Sanity-check: only use if it gives a width between 10 % and 90 % of rail
            if (ratioWidth >= railWidth * 0.1 && ratioWidth <= railWidth * 0.9) {
              itemWidthPx = ratioWidth;
            }
          }
        }
      }
    }

    this.originalItems.forEach((item) => {
      item.style.flexBasis = itemWidthPx + 'px';
      item.style.flexShrink = '0';
      item.style.flexGrow = '0';
    });

    this.loopWidth = itemWidthPx * this.originalItems.length;
    if (!this.loopWidth) {
      if (this.rebuildAttempts < 3) {
        this.rebuildAttempts += 1;
        requestAnimationFrame(() => this._rebuildTrack({ keepOffset }));
      }
      return;
    }
    this.rebuildAttempts = 0;

    if (this.isAutoEnabled) {
      const widthMultiplier = this.rail.dataset.tickerSingleMedia === 'true' ? 3 : 2;
      // The track must span at least (loopWidth + railWidth) so the viewport is
      // never empty as the offset approaches -loopWidth and wraps back to 0.
      // Also respect the widthMultiplier minimum for very short original sets.
      const minTrackWidth = Math.max(railWidth * widthMultiplier, this.loopWidth + railWidth);
      let currentWidth = this.loopWidth;

      while (currentWidth < minTrackWidth) {
        this.originalItems.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.classList.add('ticker-rail__item--clone');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          clone.querySelectorAll('a[data-pswp-item]').forEach((zoomAnchor) => {
            zoomAnchor.removeAttribute('data-pswp-item');
            zoomAnchor.setAttribute('data-pswp-item-clone', 'true');
          });
          clone.querySelectorAll('a, button, input, select, textarea, summary, [tabindex]').forEach((focusable) => {
            focusable.setAttribute('tabindex', '-1');
            focusable.setAttribute('aria-hidden', 'true');
          });
          this.track.appendChild(clone);
        });
        currentWidth += this.loopWidth;
      }
    }

    if (keepOffset && previousLoopWidth) {
      this.offset = previousRatio * this.loopWidth;
    } else {
      this.offset = this.offset || 0;
    }

    this._wrapOffset();
    if (this.isAutoEnabled) {
      this._applyTransform();
    } else {
      this._syncNativeScrollPosition();
    }
    this._setActiveItemFromOffset(true);
  }

  // ── RAF loop ──────────────────────────────────────────────────────────────

  _startRAF() {
    if (this.rafId) return;
    this.lastTimestamp = performance.now();
    this.rafId = requestAnimationFrame(this._tick);
  }

  _stopRAF() {
    if (!this.rafId) return;
    cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  _tick(timestamp) {
    // Cap delta to 50 ms to avoid large jumps after tab switches.
    const delta = Math.min(timestamp - this.lastTimestamp, 50);
    const deltaSeconds = delta / 1000;
    this.lastTimestamp = timestamp;

    if (this.settlingTarget !== null) {
      const difference = this._getCircularDifference(this.settlingTarget, this.offset);
      const step = Math.max(-40, Math.min(40, difference * this.settlingStrength));
      this.offset += step;
      if (Math.abs(difference) < 0.5 || Math.abs(step) < 0.05) {
        this.offset += difference;
        this.settlingTarget = null;
        this.settlingStrength = this.defaultSettlingStrength;
      }
    }

    if (!this.isPaused && !this.isDragging && this.settlingTarget === null && !this._isVariantAutoLocked(timestamp)) {
      // Lerp lerpedBase toward baseVelocity for silky direction-change transitions.
      // Factor 0.08 ≈ ~8 % per frame @ 60 fps → ~200 ms ramp — cinematic, never sluggish.
      this.lerpedBase += (this.baseVelocity - this.lerpedBase) * 0.08;

      const rawVelocity = this.lerpedBase + this.scrollBoost + this.momentumVelocity;
      const velocity = Math.max(-this.maxVelocity, Math.min(this.maxVelocity, rawVelocity));

      this.offset += velocity * deltaSeconds;

      // Moderate boost decay so wheel kicks feel responsive but not abrupt.
      this.scrollBoost *= 0.92;
      // Drag inertia decays smoothly for a spring-like finish.
      this.momentumVelocity *= this.momentumDecay;
      if (Math.abs(this.scrollBoost) < 0.5) this.scrollBoost = 0;
      if (Math.abs(this.momentumVelocity) < 0.5) this.momentumVelocity = 0;
    } else if (this._isVariantAutoLocked(timestamp)) {
      // Keep ticker static after variant sync until user intentionally interacts.
      this.scrollBoost = 0;
      this.momentumVelocity = 0;
    }

    this._wrapOffset();
    this._applyTransform();
    this._setActiveItemFromOffset();

    this.rafId = requestAnimationFrame(this._tick);
  }

  _applyTransform() {
    if (!this.track) return;
    if (!this.isAutoEnabled) {
      this.track.style.transform = '';
      return;
    }
    this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
  }

  _syncNativeScrollPosition() {
    if (!this.track || !this.loopWidth) return;
    this.track.style.transform = '';
    const target = ((-this.offset % this.loopWidth) + this.loopWidth) % this.loopWidth;
    this.track.scrollLeft = target;
    this.offset = -target;
  }

  _wrapOffset() {
    if (!this.loopWidth) return;
    while (this.offset <= -this.loopWidth) this.offset += this.loopWidth;
    while (this.offset > 0) this.offset -= this.loopWidth;
  }

  // ── Scroll coupling (anti-gravity wheel) ─────────────────────────────────
  // Uses the 'wheel' event instead of 'scroll' so the coupling works even when
  // the page is at scrollY=0 — trackpad/mouse overscroll upward drives the
  // ticker in reverse, creating the "anti-gravity" feel of the reference site.

  _bindScroll() {
    // Non-blocking wheel listener: keep native page scroll + pull-to-refresh,
    // while still using wheel delta to drive ticker direction/speed.
    this.rail.addEventListener('wheel', this._onWheel, { passive: true });
  }

  _onWheel(event) {
    if (!this.isAutoEnabled || this.isDragging) return;
    this._releaseVariantAutoLock();
    this.lastWheelTimestamp = performance.now();

    // Normalise deltaY across deltaMode values so trackpad and mouse wheel feel alike.
    const raw =
      event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? event.deltaY * 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? event.deltaY * 500
          : event.deltaY;
    // Boost low-amplitude trackpad values so they feel closer to mouse-wheel ticks.
    const absRaw = Math.abs(raw);
    const deviceScale = absRaw < 6 ? 10 : absRaw < 20 ? 4.5 : absRaw < 60 ? 2 : 1;
    const normalized = raw * deviceScale;
    const delta = Math.sign(normalized) * Math.min(Math.abs(normalized), 140);
    if (!delta) return;

    // Scroll UP   (delta < 0) → ticker goes RIGHT (+)
    // Scroll DOWN (delta > 0) → ticker goes LEFT  (−)
    // This is a PERSISTENT direction change: baseVelocity flips so the ticker
    // keeps going that way even after the wheel stops. lerpedBase smoothly
    // interpolates to prevent a hard velocity jump.
    const newDir = delta < 0 ? 1 : -1;
    this.baseVelocity = newDir * this.configSpeed;

    // Small immediate boost for haptic responsiveness on each wheel tick.
    const boostMag = Math.min(Math.abs(delta) * 1.1, this.configSpeed * 0.9);
    this.scrollBoost = newDir * boostMag;
  }

  // ── Drag ──────────────────────────────────────────────────────────────────

  _bindDrag() {
    this.track.addEventListener('pointerdown', this._onPointerDown);
  }

  _bindNativeScroll() {
    if (!this.track) return;
    this.track.addEventListener('scroll', this._onNativeScroll, { passive: true });
  }

  _onNativeScroll() {
    if (!this.track || this.isAutoEnabled) return;
    this.offset = -(this.track.scrollLeft || 0);
    this._setActiveItemFromOffset();
  }

  _onPointerDown(event) {
    if (!this.isAutoEnabled) return;
    if (event.button !== 0) return;
    this._releaseVariantAutoLock();
    if (event.pointerType === 'mouse') {
      event.preventDefault();
    }
    this.pointerId = event.pointerId;

    this.isDragging = true;
    this.dragLastX = event.clientX;
    this.dragLastTime = performance.now();
    this.dragVelocity = 0;
    this.momentumVelocity = 0;
    this.scrollBoost = 0;
    this.settlingTarget = null;

    this.rail.classList.add('is-dragging');
    this.track.classList.add('is-dragging');
    this.track.setPointerCapture(this.pointerId);

    this.track.addEventListener('pointermove', this._onPointerMove);
    this.track.addEventListener('pointerup', this._onPointerUp);
    this.track.addEventListener('pointercancel', this._onPointerUp);
  }

  _onPointerMove(event) {
    if (!this.isDragging) return;

    const now = performance.now();
    const dt = now - this.dragLastTime;
    const dx = event.clientX - this.dragLastX;

    if (dt > 0) {
      const instantVelocity = ((dx * this.dragMultiplier) / dt) * 1000;
      this.dragVelocity += (instantVelocity - this.dragVelocity) * this.dragVelocitySmoothing;
    }

    this.dragLastX = event.clientX;
    this.dragLastTime = now;

    const dragStep = Math.max(-this.maxDragStepPerFrame, Math.min(this.maxDragStepPerFrame, dx * this.dragMultiplier));
    this.offset += dragStep;
    this._wrapOffset();
    this._applyTransform();
    this._setActiveItemFromOffset();
  }

  _onPointerUp(event) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.pointerId = null;
    this.rail.classList.remove('is-dragging');
    this.track.classList.remove('is-dragging');
    if (event?.pointerId !== undefined && this.track.hasPointerCapture?.(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }

    this.track.removeEventListener('pointermove', this._onPointerMove);
    this.track.removeEventListener('pointerup', this._onPointerUp);
    this.track.removeEventListener('pointercancel', this._onPointerUp);

    // Persist the direction the user dragged: drag RIGHT → ticker goes right, drag LEFT → left.
    if (Math.abs(this.dragVelocity) > 30) {
      this.baseVelocity = this.dragVelocity > 0 ? this.configSpeed : -this.configSpeed;
    }

    // Momentum kick — clamped + smoothed for physical, predictable release.
    const releaseVelocity = Math.max(
      -this.maxMomentumVelocity,
      Math.min(this.maxMomentumVelocity, this.dragVelocity)
    );
    this.momentumVelocity = releaseVelocity * 0.65;
  }

  // ── Hover pause ───────────────────────────────────────────────────────────

  _bindHover() {
    this.rail.addEventListener('mouseenter', this._onMouseEnter);
    this.rail.addEventListener('mouseleave', this._onMouseLeave);
  }

  _onMouseEnter() {
    this.isPaused = true;
  }

  _onMouseLeave() {
    this.isPaused = false;
  }

  // ── Focus pause ───────────────────────────────────────────────────────────

  _bindFocus() {
    this.rail.addEventListener('focusin', this._onFocusIn);
    this.rail.addEventListener('focusout', this._onFocusOut);
  }

  _onFocusIn() {
    this.isPaused = true;
  }

  _onFocusOut() {
    // Defer so focus can move between items inside the rail without resuming.
    setTimeout(() => {
      if (this.rail && !this.rail.contains(document.activeElement)) {
        this.isPaused = false;
      }
    }, 0);
  }

  // ── IntersectionObserver ──────────────────────────────────────────────────

  _bindIntersectionObserver() {
    if (!this.isAutoEnabled) return;
    if (!('IntersectionObserver' in window)) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.isVisible = entry.isIntersecting;

        if (this.isVisible) {
          this._startRAF();
        } else {
          this._stopRAF();
        }
      },
      { threshold: 0 }
    );

    this.intersectionObserver.observe(this.rail);
  }

  _onVisibilityChange() {
    if (document.hidden) {
      this._stopRAF();
    } else if (this.isVisible && this.isAutoEnabled) {
      this._startRAF();
    }
  }

  // ── Variant sync ──────────────────────────────────────────────────────────

  scrollToMediaById(mediaId) {
    if (!this.originalItems.length) return;
    const normalizedId = String(mediaId);
    const index = this.originalItems.findIndex((item) => String(item.dataset.mediaId) === normalizedId);
    if (index === -1) return;

    if (!this.isAutoEnabled || !this.loopWidth) {
      const targetItem = this.originalItems[index];
      targetItem?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      this.gallery?.pauseInactiveMedia(index);
      return;
    }

    const targetItem = this.originalItems[index];
    const targetOffset = -targetItem.offsetLeft;
    let nearestTarget = targetOffset;
    while (nearestTarget - this.offset > this.loopWidth / 2) nearestTarget -= this.loopWidth;
    while (this.offset - nearestTarget > this.loopWidth / 2) nearestTarget += this.loopWidth;

    this._beginVariantAutoLock();
    this.settlingStrength = this.variantSettlingStrength;
    this.settlingTarget = nearestTarget;
    this.scrollBoost = 0;
    this.momentumVelocity = 0;
    this._setActiveItemFromOffset(true);
  }

  _isVariantAutoLocked(now = performance.now()) {
    if (now < this.variantAutoFreezeUntil) return true;
    if (this.variantAwaitingInteraction) return true;
    return false;
  }

  _beginVariantAutoLock() {
    const defaultDirectionVelocity = this.config.tickerDirection === 'right' ? this.configSpeed : -this.configSpeed;
    this.preVariantBaseVelocity = this.baseVelocity || defaultDirectionVelocity;
    this.baseVelocity = 0;
    this.lerpedBase = 0;
    this.scrollBoost = 0;
    this.momentumVelocity = 0;
    this.variantAutoFreezeUntil = performance.now() + this.variantAutoFreezeMs;
    this.variantAwaitingInteraction = true;
  }

  _releaseVariantAutoLock() {
    if (!this.variantAwaitingInteraction && !this.variantAutoFreezeUntil) return;
    this.variantAutoFreezeUntil = 0;
    this.variantAwaitingInteraction = false;
    const defaultDirectionVelocity = this.config.tickerDirection === 'right' ? this.configSpeed : -this.configSpeed;
    this.baseVelocity = this.preVariantBaseVelocity || defaultDirectionVelocity;
    this.lerpedBase = this.baseVelocity;
  }

  _setActiveItemFromOffset(forceUpdate = false) {
    if (!this.originalItems.length || !this.loopWidth || !this.rail) return;
    const railWidth = this.rail.getBoundingClientRect().width || 0;
    if (!railWidth) return;

    const viewportCenter = -this.offset + railWidth / 2;
    const normalizedCenter = ((viewportCenter % this.loopWidth) + this.loopWidth) % this.loopWidth;
    let runningWidth = 0;
    let activeIndex = 0;

    for (let i = 0; i < this.originalItems.length; i += 1) {
      runningWidth += this.originalItems[i].getBoundingClientRect().width;
      if (normalizedCenter <= runningWidth) {
        activeIndex = i;
        break;
      }
    }

    if (!forceUpdate && activeIndex === this.lastActiveIndex) return;
    this.lastActiveIndex = activeIndex;
    this.gallery?.pauseInactiveMedia(activeIndex);
  }

  _getCircularDifference(target, current) {
    if (!this.loopWidth) return target - current;
    let difference = target - current;
    if (difference > this.loopWidth / 2) difference -= this.loopWidth;
    if (difference < -this.loopWidth / 2) difference += this.loopWidth;
    return difference;
  }

  _enableAutoMotion() {
    if (this.isAutoActive) return;
    this.isAutoActive = true;
    this.track.classList.add('is-ready');
    this._bindScroll();
    this._bindIntersectionObserver();
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this.lastScrollY = window.scrollY;
    if (this.isVisible) this._startRAF();
  }

  _disableAutoMotion() {
    if (!this.isAutoActive) return;
    this.isAutoActive = false;
    this._stopRAF();
    this.rail?.removeEventListener('wheel', this._onWheel);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
    this.track.classList.remove('is-ready');
    this.track.style.transform = '';
    this.variantAutoFreezeUntil = 0;
    this.variantAwaitingInteraction = false;
  }

  // ── Destroy ───────────────────────────────────────────────────────────────

  destroy() {
    this._disableAutoMotion();
    this._unbindModeListeners();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    if (this.rail) {
      this.rail.removeEventListener('mouseenter', this._onMouseEnter);
      this.rail.removeEventListener('mouseleave', this._onMouseLeave);
      this.rail.removeEventListener('focusin', this._onFocusIn);
      this.rail.removeEventListener('focusout', this._onFocusOut);
    }

    if (this.track) {
      this.track.removeEventListener('pointerdown', this._onPointerDown);
      this.track.removeEventListener('pointermove', this._onPointerMove);
      this.track.removeEventListener('pointerup', this._onPointerUp);
      this.track.removeEventListener('pointercancel', this._onPointerUp);
      this.track.removeEventListener('scroll', this._onNativeScroll);

      this.track.querySelectorAll('.ticker-rail__item--clone').forEach((clone) => clone.remove());
      this.track.classList.remove('is-ready', 'is-dragging');
      this.track.style.transform = '';
    }

    if (this.rail) {
      this.rail.classList.remove('is-dragging');
    }

    this.gallery = null;
    this.rail = null;
    this.track = null;
    this.items = [];
    this.originalItems = [];
    this.loopWidth = 0;
    this.variantAutoFreezeUntil = 0;
    this.variantAwaitingInteraction = false;
  }
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.activate = this.activate.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onModelWheel = this.onModelWheel.bind(this);
  }

  connectedCallback() {
    this.playBtn = this.querySelector('.product-media__play-btn');
    this.playBtn?.addEventListener('click', this.activate);
    this.playBtn?.addEventListener('keydown', this.onKeyDown);

    // For 3D models, treat the mouse wheel over the loaded model as a zoom
    // gesture and stop it from scrolling the page. Bind on the deferred-media
    // host up front — before the model-viewer is injected on activation — so
    // the browser marks this as a non-passive (non-fast-scroll) region early
    // and even the first wheel is cancelled, not just later ones. It no-ops
    // while the model is still a poster, so the poster stays scroll-friendly.
    if (this.dataset.mediaType === 'model') {
      this.addEventListener('wheel', this.onModelWheel, {
        passive: false,
        capture: true,
      });
    }
  }

  disconnectedCallback() {
    this.playBtn?.removeEventListener('click', this.activate);
    this.playBtn?.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('wheel', this.onModelWheel, { capture: true });
  }

  onModelWheel(event) {
    const modelViewer = this.querySelector('model-viewer');
    if (modelViewer && modelViewer.loaded) event.preventDefault();
  }

  onKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.activate();
  }

  activate() {
    if (this.dataset.mediaLoaded === 'true') {
      this.pauseSiblingMedia();
      this.playLoadedMedia();
      return;
    }

    this.pauseSiblingMedia();

    const template = this.querySelector('template');
    if (!template) return;

    // Append media before class toggle so the browser can
    // establish opacity: 0 baseline before the transition fires.
    const cloned = template.content.cloneNode(true);
    this.appendChild(cloned);
    template.remove();
    this.dataset.mediaLoaded = 'true';

    // 3D models: once loaded, opt this region out of Lenis smooth-scroll so the
    // wheel zooms the model instead of the smooth-scroller moving the page.
    // Lenis then steps aside without cancelling the event, so onModelWheel
    // still cancels the *native* scroll — together they cover both the
    // smooth-scroll-on and smooth-scroll-off configurations.
    if (this.dataset.mediaType === 'model') {
      const modelViewer = this.querySelector('model-viewer');
      const optOutOfSmoothScroll = () =>
        this.setAttribute('data-lenis-prevent-wheel', '');
      if (modelViewer?.loaded) optOutOfSmoothScroll();
      else modelViewer?.addEventListener('load', optOutOfSmoothScroll, { once: true });
    }

    // Next frame: trigger CSS fade-in for media and fade-out for poster.
    requestAnimationFrame(() => {
      this.classList.add('is-active');
      this.playLoadedMedia();
    });

    // Remove poster + play button after CSS transition completes.
    const poster = this.querySelector('.product-media__poster');
    const btn = this.playBtn;
    const delay = this.getPrefersReducedMotion() ? 0 : 360;

    setTimeout(() => {
      const refocusNeeded = btn && document.activeElement === btn;
      poster?.remove();
      btn?.remove();
      this.playBtn = null;

      if (refocusNeeded) {
        const target = this.querySelector('video, model-viewer');
        if (target) {
          if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      }
    }, delay);
  }

  getPrefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  playLoadedMedia() {
    const video = this.querySelector('video');
    if (video) {
      video.play().catch(() => {});
      return;
    }

    // Re-activate an already-injected external video via postMessage.
    const iframe = this.querySelector('iframe');
    if (!iframe?.contentWindow) return;

    const src = iframe.src || '';
    if (src.includes('youtube.com') || src.includes('youtube-nocookie.com')) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
        '*'
      );
    } else if (src.includes('vimeo.com')) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ method: 'play' }),
        '*'
      );
    }
  }

  pauseSiblingMedia() {
    const gallery = this.closest('product-media-gallery');
    if (!gallery) return;

    gallery.querySelectorAll('deferred-media').forEach((item) => {
      if (item !== this && typeof item.pause === 'function') {
        item.pause();
      }
    });
  }

  pause() {
    const video = this.querySelector('video');
    if (video && !video.paused) {
      video.pause();
    }

    const iframe = this.querySelector('iframe');
    if (!iframe?.contentWindow) return;

    const src = iframe.src || '';
    if (src.includes('youtube.com') || src.includes('youtube-nocookie.com')) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*'
      );
    } else if (src.includes('vimeo.com')) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ method: 'pause' }),
        '*'
      );
    }
  }
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}
